/**
 * Minimal mutable 3D vector used by the simulation.
 *
 * The simulation deliberately does not depend on Three.js math types so the
 * renderer can be replaced without touching gameplay code. Methods mutate
 * `this` and return it so hot paths can reuse preallocated temporaries
 * instead of allocating per tick (see ARCHITECTURE.md, "Allocation policy").
 */
export class Vec3 {
  constructor(
    public x = 0,
    public y = 0,
    public z = 0,
  ) {}

  static from(v: Readonly<Vec3Like>): Vec3 {
    return new Vec3(v.x, v.y, v.z);
  }

  set(x: number, y: number, z: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  copy(v: Readonly<Vec3Like>): this {
    this.x = v.x;
    this.y = v.y;
    this.z = v.z;
    return this;
  }

  clone(): Vec3 {
    return new Vec3(this.x, this.y, this.z);
  }

  add(v: Readonly<Vec3Like>): this {
    this.x += v.x;
    this.y += v.y;
    this.z += v.z;
    return this;
  }

  sub(v: Readonly<Vec3Like>): this {
    this.x -= v.x;
    this.y -= v.y;
    this.z -= v.z;
    return this;
  }

  scale(s: number): this {
    this.x *= s;
    this.y *= s;
    this.z *= s;
    return this;
  }

  /** this += v * s */
  addScaled(v: Readonly<Vec3Like>, s: number): this {
    this.x += v.x * s;
    this.y += v.y * s;
    this.z += v.z * s;
    return this;
  }

  dot(v: Readonly<Vec3Like>): number {
    return this.x * v.x + this.y * v.y + this.z * v.z;
  }

  /** this = a × b */
  crossVectors(a: Readonly<Vec3Like>, b: Readonly<Vec3Like>): this {
    const x = a.y * b.z - a.z * b.y;
    const y = a.z * b.x - a.x * b.z;
    const z = a.x * b.y - a.y * b.x;
    this.x = x;
    this.y = y;
    this.z = z;
    return this;
  }

  lengthSq(): number {
    return this.x * this.x + this.y * this.y + this.z * this.z;
  }

  length(): number {
    return Math.sqrt(this.lengthSq());
  }

  /** Normalises in place. A zero-length vector is left at zero. */
  normalize(): this {
    const len = this.length();
    if (len > 0) this.scale(1 / len);
    return this;
  }

  /** Removes the component along unit vector `n`. */
  projectOnPlane(n: Readonly<Vec3Like>): this {
    return this.addScaled(n, -this.dot(n));
  }

  distanceTo(v: Readonly<Vec3Like>): number {
    const dx = this.x - v.x;
    const dy = this.y - v.y;
    const dz = this.z - v.z;
    return Math.sqrt(dx * dx + dy * dy + dz * dz);
  }

  lerpVectors(a: Readonly<Vec3Like>, b: Readonly<Vec3Like>, t: number): this {
    this.x = a.x + (b.x - a.x) * t;
    this.y = a.y + (b.y - a.y) * t;
    this.z = a.z + (b.z - a.z) * t;
    return this;
  }

  isFinite(): boolean {
    return Number.isFinite(this.x) && Number.isFinite(this.y) && Number.isFinite(this.z);
  }

  /**
   * Rotates this vector toward unit `target` direction by at most `maxAngle`
   * radians, preserving its length. Used for momentum-preserving steering.
   */
  rotateToward(target: Readonly<Vec3Like>, maxAngle: number, tmp: Vec3): this {
    const len = this.length();
    if (len === 0 || maxAngle <= 0) return this;
    const cos = Math.min(1, Math.max(-1, this.dot(target) / len));
    const angle = Math.acos(cos);
    if (angle <= maxAngle) {
      return this.copy(target).scale(len);
    }
    // Orthonormal basis in the plane of (this, target).
    tmp.copy(target).addScaled(this, -cos / len);
    const perpLen = tmp.length();
    if (perpLen < 1e-9) {
      // Exactly opposite: there is no unique rotation plane; refuse to turn.
      return this;
    }
    tmp.scale(1 / perpLen);
    const c = Math.cos(maxAngle);
    const s = Math.sin(maxAngle);
    const ux = this.x / len;
    const uy = this.y / len;
    const uz = this.z / len;
    this.x = (ux * c + tmp.x * s) * len;
    this.y = (uy * c + tmp.y * s) * len;
    this.z = (uz * c + tmp.z * s) * len;
    return this;
  }
}

export interface Vec3Like {
  x: number;
  y: number;
  z: number;
}

export interface QuatLike {
  x: number;
  y: number;
  z: number;
  w: number;
}

/** Quaternion rotating +Y onto unit vector `up` (shortest arc). */
export function quatFromUp(up: Readonly<Vec3Like>, out: QuatLike): QuatLike {
  // Rotation from a=(0,1,0) to b=up: axis = a×b = (up.z, 0, -up.x), w = 1 + a·b
  const w = 1 + up.y;
  if (w < 1e-8) {
    out.x = 1;
    out.y = 0;
    out.z = 0;
    out.w = 0;
    return out;
  }
  const x = up.z;
  const z = -up.x;
  const inv = 1 / Math.sqrt(x * x + z * z + w * w);
  out.x = x * inv;
  out.y = 0;
  out.z = z * inv;
  out.w = w * inv;
  return out;
}

export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Frame-rate independent exponential approach factor: 1 - e^(-rate*dt). */
export function approachFactor(rate: number, dt: number): number {
  return 1 - Math.exp(-rate * dt);
}
